'use client';

import PlacesAutocomplete from '@c/forms/controls/PlacesAutocomplete';
import {
  CircleF,
  GoogleMap,
  MarkerF,
  useLoadScript,
} from '@react-google-maps/api';
import Select from '@ui/Select';
import { useToastContext } from 'context/ToastContext';
import { LocateFixed, SearchIcon } from 'lucide-react';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { getGeocode, getLatLng } from 'use-places-autocomplete';

type LatLng = { lat: number; lng: number };
export type FilterLocation = { lat: number; lng: number; address: string };

const MAPS_LIBRARIES = ['places'] as 'places'[];
// Miami, FL — the default pin shown when no location is selected yet.
const DEFAULT_LOCATION: FilterLocation = {
  lat: 25.7617,
  lng: -80.1918,
  address: 'Miami, Florida',
};
const PLACE_ZOOM = 11;
const RADIUS_OPTIONS = [10, 25, 50, 100, 250, 500];
const MILES_TO_METERS = 1609.34;

// Bounding box of the radius circle, so the map can frame it on change.
function getRadiusBounds(center: LatLng, radiusMeters: number) {
  const latDelta = radiusMeters / 111_320;
  const lngDelta =
    radiusMeters / (111_320 * Math.cos((center.lat * Math.PI) / 180));
  return {
    north: center.lat + latDelta,
    south: center.lat - latDelta,
    east: center.lng + lngDelta,
    west: center.lng - lngDelta,
  };
}

interface MobileLocationFilterProps {
  location: FilterLocation | null | undefined;
  setLocation: (location: FilterLocation) => void;
  radius: number;
  setRadius: (radius: number) => void;
  popularCities: string[];
}

export interface MobileLocationFilterHandle {
  /** Commit the staged location + radius to the parent filters. The drawer's
   *  Apply button calls this. */
  apply: () => void;
}

// Facebook-style mobile location filter — map on top, location card with
// "Locate me" / "Edit" actions, then a "Suggested for you" list. The Google
// Maps SDK is fetched lazily: this component only mounts when the user opens
// the Location page in the filter drawer. All interactions stage local
// "pending" state; nothing is committed to the filter until `apply()` is
// invoked via the imperative handle.
const MobileLocationFilter = forwardRef<
  MobileLocationFilterHandle,
  MobileLocationFilterProps
>(({ location, setLocation, radius, setRadius, popularCities }, ref) => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || '',
    libraries: MAPS_LIBRARIES,
  });
  const { showErrorToast } = useToastContext();

  const initialLocation = location ?? DEFAULT_LOCATION;
  const initialRadius = radius && RADIUS_OPTIONS.includes(radius) ? radius : 25;

  const [pendingLocation, setPendingLocation] =
    useState<FilterLocation>(initialLocation);
  const [pendingRadius, setPendingRadius] = useState(initialRadius);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [locating, setLocating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [initialCenter] = useState<LatLng>({
    lat: initialLocation.lat,
    lng: initialLocation.lng,
  });

  const marker: LatLng = {
    lat: pendingLocation.lat,
    lng: pendingLocation.lng,
  };

  const radiusMeters = pendingRadius * MILES_TO_METERS;

  useImperativeHandle(ref, () => ({
    apply: () => {
      setLocation(pendingLocation);
      setRadius(pendingRadius);
    },
  }));

  // Frame the map around the radius circle whenever the center or radius
  // changes, so the whole circle stays visible.
  useEffect(() => {
    if (!map) return;
    if (!marker.lat && !marker.lng) return;
    map.fitBounds(getRadiusBounds(marker, radiusMeters), 32);
  }, [map, marker.lat, marker.lng, radiusMeters]);

  const stageLocation = (coords: LatLng, address: string) => {
    setPendingLocation({ lat: coords.lat, lng: coords.lng, address });
  };

  const handleLocateMe = () => {
    if (!navigator.geolocation) {
      showErrorToast('Geolocation is not supported');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const coords = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };
        let address = pendingLocation.address;
        try {
          const res = await fetch(
            `/api/geocode?lat=${coords.lat}&lng=${coords.lng}`
          );
          const data = await res.json();
          if (data?.address) address = data.address;
        } catch {
          // reverse geocode is best-effort — keep the pin regardless
        }
        stageLocation(coords, address);
        setLocating(false);
      },
      () => {
        showErrorToast('Failed to get your location');
        setLocating(false);
      }
    );
  };

  const handleCitySelect = async (city: string) => {
    if (!isLoaded) {
      setPendingLocation({ lat: 0, lng: 0, address: city });
      return;
    }
    try {
      const results = await getGeocode({ address: city });
      const { lat, lng } = getLatLng(results[0]);
      setPendingLocation({ lat, lng, address: city });
    } catch {
      // geocode is best-effort — still stage the address so the user sees
      // the city in the card even if the map can't recenter.
      setPendingLocation({ lat: 0, lng: 0, address: city });
    }
  };

  return (
    <div className="flex flex-col gap-[2.4rem]">
      <div className="relative h-[24rem] w-full overflow-hidden rounded-[1.6rem] bg-[#F1F1F1]">
        {isLoaded ? (
          <GoogleMap
            mapContainerClassName="h-full w-full"
            center={initialCenter}
            zoom={PLACE_ZOOM}
            onLoad={setMap}
            options={{
              disableDefaultUI: true,
              clickableIcons: false,
              gestureHandling: 'greedy',
            }}
          >
            <CircleF
              center={marker}
              radius={radiusMeters}
              options={{
                fillColor: '#000000',
                fillOpacity: 0.15,
                strokeColor: '#000000',
                strokeOpacity: 0.25,
                strokeWeight: 1,
                clickable: false,
              }}
            />
            <MarkerF position={marker} />
          </GoogleMap>
        ) : (
          <div className="h-full w-full animate-pulse bg-[#F1F1F1]" />
        )}
      </div>

      <div className="flex flex-col gap-[1.6rem] rounded-[1.6rem] bg-white p-[1.6rem] shadow-sm ring-1 ring-[#EAEAEA]">
        {editing ? (
          <div className="flex flex-col gap-[1.2rem]">
            {isLoaded && (
              <PlacesAutocomplete
                requireLatLng
                MXAuto={false}
                hideSearchIcon
                setSelected={({ lat, lng, address }) => {
                  if (lat != null && lng != null) {
                    stageLocation({ lat, lng }, address);
                  } else {
                    setPendingLocation({ lat: 0, lng: 0, address });
                  }
                  setEditing(false);
                }}
                className="!h-[5.5rem] !rounded-[1.6rem]"
              />
            )}
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="self-start text-[1.6rem] font-semibold text-brand-secondary"
            >
              Cancel
            </button>
          </div>
        ) : (
          <>
            <p className="text-[2rem] font-semibold text-black">
              {pendingLocation.address}
            </p>
            <div className="flex items-center gap-[1.2rem]">
              <button
                type="button"
                onClick={handleLocateMe}
                disabled={locating || !isLoaded}
                className="flex flex-1 items-center justify-center gap-[0.8rem] rounded-full bg-brand-secondary px-[1.6rem] py-[1.2rem] text-[1.6rem] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                <LocateFixed className="h-[1.8rem] w-[1.8rem]" />
                {locating ? 'Locating…' : 'Locate me'}
              </button>
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="flex flex-1 items-center justify-center gap-[0.8rem] rounded-full bg-[#F1F1F1] px-[1.6rem] py-[1.2rem] text-[1.6rem] font-semibold text-black transition-colors hover:bg-zinc-200"
              >
                <SearchIcon className="h-[1.8rem] w-[1.8rem]" />
                Search
              </button>
            </div>
          </>
        )}
      </div>

      <div className="flex flex-col gap-[0.8rem]">
        <span className="text-[1.8rem] font-semibold text-black">Radius</span>
        <Select
          allowEmpty={false}
          value={String(pendingRadius)}
          className="!h-[5.5rem] !rounded-[2rem]"
          options={RADIUS_OPTIONS.map((mi) => ({
            id: String(mi),
            label: `${mi} miles`,
            value: String(mi),
          }))}
          onChange={(option) => setPendingRadius(Number(option.id))}
        />
      </div>

      <div className="flex flex-col gap-[1.2rem]">
        <span className="text-[1.8rem] font-semibold text-black">
          Popular cities
        </span>
        <div className="flex flex-col rounded-[1.6rem] bg-white shadow-sm ring-1 ring-[#EAEAEA]">
          {popularCities.map((city, i) => (
            <button
              key={city}
              type="button"
              onClick={() => handleCitySelect(city)}
              className={`flex items-center gap-[1.2rem] px-[1.6rem] py-[1.4rem] text-left ${
                i < popularCities.length - 1 ? 'border-b border-[#EAEAEA]' : ''
              }`}
            >
              <SearchIcon className="h-[1.8rem] w-[1.8rem] text-zinc-500" />
              <span className="text-[1.6rem] font-medium text-black">
                {city}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
});

MobileLocationFilter.displayName = 'MobileLocationFilter';

export default MobileLocationFilter;
